import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Loader2, X, GitBranch, Plus, Trash2, Check, ArrowDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useSite } from '../../context/SiteContext';

/**
 * Admin modal to configure approval-stage templates per document type AND site.
 *
 * Backend:
 *   GET /api/documents/workflow-config   -> { templates: [{document_type, site_id, stages}], document_types }
 *   PUT /api/documents/workflow-config   { document_type, site_id, stages: [{name, approver_id, approver_name}] }
 *
 * Stages assign specific users (site-filtered). Empty stages => legacy
 * single-reviewer behaviour. site_id '__all__' = applies to every site.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api/documents`;
const USERS_API = `${process.env.REACT_APP_BACKEND_URL}/api/users`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const DEPARTMENTS_API = `${process.env.REACT_APP_BACKEND_URL}/api/departments`;
const keyOf = (site, dept, type) => `${site}|${dept}|${type}`;

const WorkflowSettings = ({ open, onClose }) => {
  const { availableSites = [] } = useSite() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  const [documentTypes, setDocumentTypes] = useState([]);
  const [templates, setTemplates] = useState({}); // "site|dept|type" -> stages[]
  const [users, setUsers] = useState([]);          // users for the selected site
  const [departments, setDepartments] = useState([]);
  const [siteId, setSiteId] = useState('__all__');
  const [deptId, setDeptId] = useState('__all__');
  const [docType, setDocType] = useState('__default__');
  const [stages, setStages] = useState([]); // [{name, approver_id, approver_name}]

  // Users to assign as stage approvers (site-filtered), sorted by display name.
  const userOptions = useMemo(() => {
    return [...(users || [])].sort((a, b) =>
      (a.name || a.email || '').localeCompare(b.name || b.email || ''));
  }, [users]);

  const fetchUsersForSite = useCallback(async (site) => {
    const url = site && site !== '__all__' ? `${USERS_API}?site_id=${site}` : USERS_API;
    try {
      const res = await axios.get(url, authHeaders());
      setUsers(res.data || []);
    } catch (e) {
      setUsers([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cfgRes, deptRes] = await Promise.all([
        axios.get(`${API}/workflow-config`, authHeaders()),
        axios.get(DEPARTMENTS_API, authHeaders()).catch(() => ({ data: { departments: [] } })),
      ]);
      const byKey = {};
      (cfgRes.data.templates || []).forEach((t) => {
        byKey[keyOf(t.site_id || '__all__', t.department_id || '__all__', t.document_type)] = t.stages || [];
      });
      setTemplates(byKey);
      setDepartments(deptRes.data.departments || []);
      setDocumentTypes(cfgRes.data.document_types || []);
      setSiteId('__all__');
      setDeptId('__all__');
      setDocType('__default__');
      setStages((byKey[keyOf('__all__', '__all__', '__default__')] || []).map((s) => ({ ...s })));
      await fetchUsersForSite('__all__');
    } catch (e) {
      setError('Failed to load workflow configuration.');
    } finally {
      setLoading(false);
    }
  }, [fetchUsersForSite]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const loadStages = (site, dept, type) => {
    setStages((templates[keyOf(site, dept, type)] || []).map((s) => ({ ...s })));
  };

  const selectSite = async (site) => {
    setSavedMsg('');
    setSiteId(site);
    loadStages(site, deptId, docType);
    await fetchUsersForSite(site);
  };
  const selectDept = (dept) => {
    setSavedMsg('');
    setDeptId(dept);
    loadStages(siteId, dept, docType);
  };
  const selectType = (type) => {
    setSavedMsg('');
    setDocType(type);
    loadStages(siteId, deptId, type);
  };

  const updateStage = (idx, field, value) => {
    setSavedMsg('');
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  const addStage = () => { setSavedMsg(''); setStages((prev) => [...prev, { name: '', approver_id: '', approver_name: '' }]); };

  const setStageApprover = (idx, userId) => {
    setSavedMsg('');
    const u = (users || []).find((x) => x.id === userId);
    setStages((prev) => prev.map((s, i) => (i === idx
      ? { ...s, approver_id: userId, approver_name: u ? (u.name || u.email) : '' }
      : s)));
  };
  const removeStage = (idx) => { setSavedMsg(''); setStages((prev) => prev.filter((_, i) => i !== idx)); };

  const save = async () => {
    setSaving(true);
    setError('');
    setSavedMsg('');
    try {
      const res = await axios.put(
        `${API}/workflow-config`,
        { document_type: docType, site_id: siteId, department_id: deptId, stages },
        authHeaders()
      );
      setTemplates((prev) => ({ ...prev, [keyOf(siteId, deptId, docType)]: res.data.stages }));
      setSavedMsg('Workflow saved.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save workflow.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Approval Workflow</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
                <select
                  value={siteId}
                  onChange={(e) => selectSite(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="__all__">All sites (default)</option>
                  {availableSites.map((s) => (
                    <option key={s.site_id} value={s.site_id}>{s.site_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select
                  value={deptId}
                  onChange={(e) => selectDept(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="__all__">All departments (default)</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document type</label>
                <select
                  value={docType}
                  onChange={(e) => selectType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="__default__">Default (all document types)</option>
                  {documentTypes.map((t) => (
                    <option key={t} value={t}>
                      {t.replace('_', ' ')}{templates[keyOf(siteId, deptId, t)] && templates[keyOf(siteId, deptId, t)].length ? ' — customised' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              Configure stages for a specific Site and/or Department, or leave them on “All …” for broader defaults.
              A more specific template wins; Site is weighted above Department. Approvers are filtered to the
              selected site’s users. No stages = single-reviewer (legacy) flow.
            </p>

            {/* Stages */}
            <div className="space-y-2">
              {stages.length === 0 && (
                <p className="text-sm text-gray-500 italic">No stages — this type uses the single-reviewer flow.</p>
              )}
              {stages.map((stage, idx) => (
                <div key={idx}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-400 w-6">{idx + 1}</span>
                    <input
                      type="text"
                      placeholder="Stage name (e.g. Review)"
                      value={stage.name}
                      onChange={(e) => updateStage(idx, 'name', e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <select
                      value={stage.approver_id || ''}
                      onChange={(e) => setStageApprover(idx, e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">Approver (user)…</option>
                      {userOptions.map((u) => (
                        <option key={u.id} value={u.id}>{u.name || u.email}</option>
                      ))}
                    </select>
                    <button onClick={() => removeStage(idx)} className="text-gray-400 hover:text-red-600 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {idx < stages.length - 1 && (
                    <div className="flex justify-center text-gray-300"><ArrowDown className="w-3 h-3" /></div>
                  )}
                </div>
              ))}
              <Button variant="outline" onClick={addStage} className="mt-1">
                <Plus className="w-4 h-4 mr-2" /> Add stage
              </Button>
            </div>

            {error && <div className="text-sm text-red-600">{error}</div>}
            {savedMsg && <div className="flex items-center gap-1 text-sm text-green-700"><Check className="w-4 h-4" /> {savedMsg}</div>}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowSettings;
